import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PENDING_WRITER_PROMPT_KEY, clearPendingIntent, storePendingIntent } from '@/lib/auth/pendingIntent';
import { createWriterDraft } from '@/lib/writer/createWriterDraft';
import { toast } from 'sonner';

export function useWriterPromptStart() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const submitPrompt = useCallback(async (prompt: string) => {
    storePendingIntent(PENDING_WRITER_PROMPT_KEY, prompt);

    if (!user) {
      storePendingIntent('redirectAfterAuth', '/write');
      setAuthOpen(true);
      return;
    }

    if (busy) return;
    setBusy(true);
    const projectId = await createWriterDraft(user.id);
    if (!projectId) {
      clearPendingIntent(PENDING_WRITER_PROMPT_KEY);
      toast.error('Could not create draft');
      setBusy(false);
      return;
    }

    navigate(`/write/${projectId}`);
  }, [busy, navigate, user]);

  const continueAfterAuth = useCallback(() => {
    setAuthOpen(false);
    navigate('/write');
  }, [navigate]);

  const preserveOAuthDestination = useCallback(() => {
    storePendingIntent('redirectAfterAuth', '/write');
  }, []);

  return {
    authOpen,
    setAuthOpen,
    busy,
    submitPrompt,
    continueAfterAuth,
    preserveOAuthDestination,
  };
}